/**
 * Extensions Redux Slice
 * 
 * This slice manages state for:
 * - Games (all games, categories, featured games)
 * - Categories
 * - SEO data
 * - File operations (uploads, deletions)
 * 
 * Uses Redux Toolkit for async actions and state management
 */

import { createSlice } from "@reduxjs/toolkit";
import { createAsyncThunk } from "@reduxjs/toolkit";

// Import API functions
import {
  getAllGames,
  getAllCategories,
  updategamedetail,
  deletegamedetail,
  addcategory,
  getAllCategory,
  deleteCategory,
  addgame,
  getfeaturedGames,
  setfeaturedpriority,
  deletefilepath,
  updategamedetailformdata,
  Seoadd,
  Seoget,
  Seoupdate,
  Seodelete,
} from "../api/extapi";

// ========================================
// Async Thunks - Games
// ========================================

/**
 * Fetch all games or games by category
 * @param {Object} data - Query data (e.g., { cat: "all" } or { cat: "categoryId" })
 */
export const allGames = createAsyncThunk("games/all", async (data) => {
  try {
    const response = await getAllGames(data);
    return response.data;
  } catch (error) {
    console.log(error.message, "errorrrrr");
    throw error;
  }
});

/**
 * Add a new game
 * @param {FormData} formdata - Form data containing game information and files
 */
export const addGame = createAsyncThunk(
  "/games/add",
  async (formdata, { rejectWithValue }) => {
    try {
      const response = await addgame(formdata);
      return response?.data;
    } catch (error) {
      console.error("Error in addGame:", error);
      return rejectWithValue(error);
    }
  }
);

/**
 * Update game details without file uploads
 * @param {Object} formdata - Game data to update
 */
export const updateGameDetail = createAsyncThunk(
  "/games/update",
  async (formdata) => {
    try {
      const response = await updategamedetail(formdata);
      return response.data;
    } catch (error) {
      console.log(error, "err");
      throw error;
    }
  }
);

/**
 * Update game details with file uploads (multipart/form-data)
 * @param {FormData} formdata - Form data containing game updates and files
 */
export const updateGameDetailformData = createAsyncThunk(
  "/games/updateform",
  async (formdata) => {
    try {
      const response = await updategamedetailformdata(formdata);
      return response.data;
    } catch (error) {
      console.log(error, "err");
      throw error;
    }
  }
);

/**
 * Delete a game
 * @param {Object} formdata - Object containing game ID (e.g., { id: "gameId" })
 */
export const deleteGameDetail = createAsyncThunk(
  "/games/delete",
  async (formdata, { rejectWithValue }) => {
    try {
      const response = await deletegamedetail(formdata);
      return response.data;
    } catch (error) {
      console.log(error, "err");
      return rejectWithValue(error);
    }
  }
);

/**
 * Delete a file from filesystem and update database
 * @param {Object} data - Object containing pathtodelete, field, and documentId
 */
export const deleteFilePath = createAsyncThunk(
  "/games/deletepath",
  async (data, { rejectWithValue }) => {
    try {
      const response = await deletefilepath(data);
      return response.data;
    } catch (error) {
      console.log(error, "err");
      return rejectWithValue(error);
    }
  }
);

// ========================================
// Async Thunks - Categories
// ========================================

/**
 * Fetch all categories
 */
export const allCategories = createAsyncThunk("categories/all", async (data) => {
  try {
    const response = await getAllCategories(data);
    return response.data;
  } catch (error) {
    console.log(error.message, "errorrrrr");
    throw error;
  }
});

/**
 * Fetch all categories (alternative endpoint)
 */
export const getallcategory = createAsyncThunk("category/all", async () => {
  try {
    const response = await getAllCategory();
    return response.data;
  } catch (error) {
    console.log(error.message, "errorrrrr");
    throw error;
  }
});

/**
 * Add a new category
 * @param {FormData} formdata - Form data containing category information and logo
 */
export const addCategory = createAsyncThunk(
  "/category/addcategory",
  async (formdata, { rejectWithValue }) => {
    try {
      const response = await addcategory(formdata);
      return response?.data;
    } catch (error) {
      console.error("Error in addCategory:", error);
      return rejectWithValue(error);
    }
  }
);

/**
 * Delete a category
 * @param {Object} formdata - Object containing category ID
 */
export const deletecategory = createAsyncThunk(
  "category/delete",
  async (formdata) => {
    try {
      const response = await deleteCategory(formdata);
      return response.data;
    } catch (error) {
      console.log(error.message, "errorrrrr");
      throw error;
    }
  }
);

// ========================================
// Async Thunks - Featured Games
// ========================================

/**
 * Fetch all featured games
 */
export const getFeaturedGames = createAsyncThunk("featured/all", async () => {
  try {
    const response = await getfeaturedGames();
    return response.data;
  } catch (error) {
    console.log(error.message, "errorrrrr");
    throw error;
  }
});

/**
 * Set priority for featured games
 * @param {Object} formdata - Form data containing game priorities
 */
export const setFeaturedPriority = createAsyncThunk(
  "featured/priority",
  async (formdata) => {
    try {
      const response = await setfeaturedpriority(formdata);
      return response.data;
    } catch (error) {
      console.log(error.message, "errorrrrr");
      throw error;
    }
  }
);

// ========================================
// Async Thunks - SEO
// ========================================

/**
 * Add SEO data
 * @param {Object} data - SEO data to add
 */
export const addSEO = createAsyncThunk("seo/add", async (data) => {
  try {
    const response = await Seoadd(data);
    return response.data;
  } catch (error) {
    console.log(error.message, "errorrrrr");
    throw error;
  }
});

/**
 * Get SEO data
 * @param {Object} data - Query data for SEO
 */
export const getSEO = createAsyncThunk("seo/get", async (data) => {
  try {
    const response = await Seoget(data);
    return response.data;
  } catch (error) {
    console.log(error.message, "errorrrrr");
    throw error;
  }
});

/**
 * Update SEO data
 * @param {Object} data - Updated SEO data
 */
export const updateSEO = createAsyncThunk("seo/update", async (data) => {
  try {
    const response = await Seoupdate(data);
    return response.data;
  } catch (error) {
    console.log(error.message, "errorrrrr");
    throw error;
  }
});

/**
 * Delete SEO data
 * @param {Object} data - SEO data to delete
 */
export const deleteSEO = createAsyncThunk("seo/delete", async (data) => {
  try {
    const response = await Seodelete(data);
    return response.data;
  } catch (error) {
    console.log(error.message, "errorrrrr");
    throw error;
  }
});

// ========================================
// Redux Slice
// ========================================

/**
 * Initial state for the extensions slice
 */
const extSlice = createSlice({
  name: "extSlice",
  initialState: {
    table: null, // Games data
    categories: null, // Categories data
    message: null, // General messages
    tableAddedExt: null,
    tableUpdatedExt: null,
    tableRemovedExt: null,
    tableExtbyCate: null,
    tableMostDownloadedExt: null,
    tablemanifestExt: null,
    allcategory: null, // All categories
    featuredgames: null, // Featured games
    prioritymessage: null, // Priority update messages
    seodata: null, // SEO data
  },

  // Reducers are handled by extraReducers (async actions)
  reducers: {},

  // Handle async action states (pending, fulfilled, rejected)
  extraReducers: (builder) => {
    // ========================================
    // Games Reducers
    // ========================================

    // Fetch all games
    builder.addCase(allGames.fulfilled, (state, action) => {
      state.table = action.payload;
    });
    builder.addCase(allGames.rejected, (state, action) => {
      state.table = null;
    });

    // Add game
    builder.addCase(addGame.fulfilled, (state, action) => {
      console.log(action.payload, "action");
      // Game added successfully
    });
    builder.addCase(addGame.rejected, (state, action) => {
      state.message = null;
    });

    // Update game details (without files)
    builder.addCase(updateGameDetail.fulfilled, (state, action) => {
      state.table = action.payload;
    });
    builder.addCase(updateGameDetail.rejected, (state, action) => {
      state.table = null;
    });

    // Update game details (with files)
    builder.addCase(updateGameDetailformData.fulfilled, (state, action) => {
      state.table = action.payload;
    });
    builder.addCase(updateGameDetailformData.rejected, (state, action) => {
      state.table = null;
    });

    // Delete game
    builder.addCase(deleteGameDetail.fulfilled, (state, action) => {
      state.table = action.payload;
    });
    builder.addCase(deleteGameDetail.rejected, (state, action) => {
      state.table = null;
    });

    // Delete file path
    builder.addCase(deleteFilePath.fulfilled, (state, action) => {
      state.message = action.payload;
    });
    builder.addCase(deleteFilePath.rejected, (state, action) => {
      state.message = null;
    });

    // ========================================
    // Categories Reducers
    // ========================================

    // Fetch all categories (primary)
    builder.addCase(allCategories.fulfilled, (state, action) => {
      state.categories = action.payload;
    });
    builder.addCase(allCategories.rejected, (state, action) => {
      state.categories = null;
    });

    // Fetch all categories (alternative)
    builder.addCase(getallcategory.fulfilled, (state, action) => {
      state.allcategory = action.payload;
    });
    builder.addCase(getallcategory.rejected, (state, action) => {
      state.allcategory = null;
    });

    // Add category
    builder.addCase(addCategory.fulfilled, (state, action) => {
      state.message = "Category Added";
    });
    builder.addCase(addCategory.rejected, (state, action) => {
      state.message = null;
    });

    // ========================================
    // Featured Games Reducers
    // ========================================

    // Fetch featured games
    builder.addCase(getFeaturedGames.fulfilled, (state, action) => {
      state.featuredgames = action.payload;
    });
    builder.addCase(getFeaturedGames.rejected, (state, action) => {
      state.featuredgames = null;
    });

    // Set featured priority
    builder.addCase(setFeaturedPriority.fulfilled, (state, action) => {
      state.prioritymessage = action.payload;
    });
    builder.addCase(setFeaturedPriority.rejected, (state, action) => {
      state.prioritymessage = null;
    });

    // ========================================
    // SEO Reducers
    // ========================================

    // Add SEO
    builder.addCase(addSEO.fulfilled, (state, action) => {
      state.message = action.payload;
    });
    builder.addCase(addSEO.rejected, (state, action) => {
      state.table = null;
    });

    // Get SEO
    builder.addCase(getSEO.fulfilled, (state, action) => {
      state.seodata = action.payload;
    });
    builder.addCase(getSEO.rejected, (state, action) => {
      state.seodata = null;
    });

    // Update SEO
    builder.addCase(updateSEO.fulfilled, (state, action) => {
      state.message = action.payload;
    });
    builder.addCase(updateSEO.rejected, (state, action) => {
      state.prioritymessage = null;
    });

    // Delete SEO
    builder.addCase(deleteSEO.fulfilled, (state, action) => {
      state.message = action.payload;
    });
    builder.addCase(deleteSEO.rejected, (state, action) => {
      state.message = null;
    });
  },
});

export default extSlice.reducer;
